import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, doc, setDoc, updateDoc, deleteDoc, serverTimestamp, getDoc, getDocs, getFirestore } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useFirebase } from './FirebaseContext';

const AddressContext = createContext();

export function AddressProvider({ children }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { db, auth } = useFirebase();

  // Load addresses when user changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        loadAddresses(user.uid);
      } else {
        setAddresses([]);
      }
    });

    return unsubscribe;
  }, [auth]);

  // Reload addresses when component mounts or user changes
  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      loadAddresses(user.uid);
    }
  }, [auth?.currentUser]);

  const loadAddresses = async (userId) => {
    if (!userId) {
      console.log('No userId provided to loadAddresses');
      return;
    }

    console.log('Loading addresses for userId:', userId);
    setLoading(true);
    try {
      // iOS saves addresses as a SUBCOLLECTION (users/{uid}/addresses/{id})
      // keyed by the address's own UUID; older web builds also keep an
      // `addresses` array on the user doc. Read both and merge by id so
      // iOS-saved addresses ("Home", "Work") appear here — subcollection wins.
      const userDocRef = doc(db, 'users', userId);
      const [userDoc, subSnap] = await Promise.all([
        getDoc(userDocRef),
        getDocs(collection(db, 'users', userId, 'addresses')),
      ]);

      const merged = new Map();
      const normalize = (address, index) => ({
        id: address.id || `address_${index}`,
        ...address,
        createdAt: address.createdAt?.toDate?.() || address.createdAt || new Date(),
        coordinates: address.coordinates || { latitude: 0, longitude: 0 }
      });

      (userDoc.exists() ? (userDoc.data().addresses || []) : [])
        .forEach((a, i) => merged.set(a.id || `address_${i}`, normalize(a, i)));
      subSnap.docs.forEach(d => merged.set(d.id, normalize({ id: d.id, ...d.data() })));

      const formattedAddresses = [...merged.values()];
      setAddresses(formattedAddresses);
      console.log('Addresses loaded (array + iOS subcollection):', formattedAddresses.length);
    } catch (err) {
      console.error('Error loading addresses:', err);
      setError(err.message);
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  };

  const addAddress = async (addressData) => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    setLoading(true);
    setError(null);

    try {
      const address = {
        // iOS Address.id = UUID().uuidString — same format so docs are
        // interchangeable between platforms.
        id: (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`).toUpperCase(),
        ...addressData,
        userId: user.uid,
        createdAt: new Date(),
        isDefault: addresses.length === 0 // First address is default
      };

      // Get current user document
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const currentAddresses = userData.addresses || [];
        
        // If setting as default, update other addresses
        if (address.isDefault && currentAddresses.length > 0) {
          currentAddresses.forEach(addr => {
            if (addr.isDefault) {
              addr.isDefault = false;
            }
          });
        }
        
        // Add new address to array (for web app compatibility)
        currentAddresses.push(address);
        
        // Update user document with addresses array
        await updateDoc(userDocRef, {
          addresses: currentAddresses
        });

        // Also add to addresses subcollection (for iOS compatibility)
        const addressDocRef = doc(db, 'users', user.uid, 'addresses', address.id);
        await setDoc(addressDocRef, {
          ...address,
          createdAt: serverTimestamp()
        });
        
        console.log('Address added to both array and subcollection for iOS sync');
        
        // Reload addresses
        await loadAddresses(user.uid);
      }

      return address.id;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateAddress = async (addressId, addressData) => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    console.log('Updating address:', addressId, addressData);
    setLoading(true);
    setError(null);

    try {
      // Get current user document
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const currentAddresses = userData.addresses || [];
        console.log('Current addresses before update:', currentAddresses);
        
        // If setting as default, update other addresses
        if (addressData.isDefault) {
          currentAddresses.forEach(addr => {
            if (addr.id !== addressId && addr.isDefault) {
              addr.isDefault = false;
            }
          });
        }
        
        // Find and update the address in array
        const addressIndex = currentAddresses.findIndex(addr => addr.id === addressId);
        if (addressIndex !== -1) {
          currentAddresses[addressIndex] = {
            ...currentAddresses[addressIndex],
            ...addressData,
            updatedAt: new Date()
          };
          
          console.log('Updated addresses array:', currentAddresses);
          
          // Update user document with addresses array
          await updateDoc(userDocRef, {
            addresses: currentAddresses
          });

          // Also update in addresses subcollection (for iOS compatibility)
          // First, get the existing address from subcollection to preserve all fields
          const addressDocRef = doc(db, 'users', user.uid, 'addresses', addressId);
          const existingAddressDoc = await getDoc(addressDocRef);
          
          if (existingAddressDoc.exists()) {
            // Merge existing data with new updates
            const existingData = existingAddressDoc.data();
            await updateDoc(addressDocRef, {
              ...existingData,
              ...addressData,
              updatedAt: serverTimestamp()
            });
          } else {
            // If doesn't exist in subcollection, create it
            await setDoc(addressDocRef, {
              ...currentAddresses[addressIndex],
              ...addressData,
              createdAt: serverTimestamp()
            });
          }
          
          console.log('Address updated in both array and subcollection for iOS sync');
          
          // Update local state immediately
          const formattedAddresses = currentAddresses.map((address, index) => ({
            id: address.id || `address_${index}`,
            ...address,
            createdAt: address.createdAt?.toDate?.() || new Date(),
            coordinates: address.coordinates || { latitude: 0, longitude: 0 }
          }));
          
          setAddresses(formattedAddresses);
          console.log('Local addresses updated after update:', formattedAddresses);
          
          // Also reload to make sure we're in sync
          setTimeout(() => loadAddresses(user.uid), 500);
        } else {
          throw new Error('Address not found');
        }
      }
    } catch (err) {
      console.error('Update address error:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteAddress = async (addressId) => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    console.log('Deleting address:', addressId);
    setLoading(true);
    setError(null);

    try {
      // Get current user document
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const currentAddresses = userData.addresses || [];
        console.log('Current addresses before delete:', currentAddresses);
        
        // Remove the address from array
        const updatedAddresses = currentAddresses.filter(addr => addr.id !== addressId);
        console.log('Updated addresses after delete:', updatedAddresses);
        
        // Update user document with addresses array
        await updateDoc(userDocRef, {
          addresses: updatedAddresses
        });

        // Also delete from addresses subcollection (for iOS compatibility)
        const addressDocRef = doc(db, 'users', user.uid, 'addresses', addressId);
        await deleteDoc(addressDocRef);
        
        console.log('Address deleted from both array and subcollection for iOS sync');
        
        // Update local state immediately (don't wait for reload)
        const formattedAddresses = updatedAddresses.map((address, index) => ({
          id: address.id || `address_${index}`,
          ...address,
          createdAt: address.createdAt?.toDate?.() || new Date(),
          coordinates: address.coordinates || { latitude: 0, longitude: 0 }
        }));
        
        setAddresses(formattedAddresses);
        console.log('Local addresses updated:', formattedAddresses);
        
        // Also reload to make sure we're in sync
        setTimeout(() => loadAddresses(user.uid), 500);
      }
    } catch (err) {
      console.error('Delete address error:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getDefaultAddress = () => {
    return addresses.find(addr => addr.isDefault) || addresses[0];
  };

  const formatAddress = (address) => {
    if (!address) return '';
    const components = [
      address.street,
      address.apartment && `Apt: ${address.apartment}`,
      address.parish,
      address.island,
      address.landmark && `Near: ${address.landmark}`
    ].filter(Boolean);
    return components.join(', ');
  };

  const value = {
    addresses,
    loading,
    error,
    addAddress,
    updateAddress,
    deleteAddress,
    getDefaultAddress,
    formatAddress
  };

  return (
    <AddressContext.Provider value={value}>
      {children}
    </AddressContext.Provider>
  );
}

export function useAddress() {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error('useAddress must be used within an AddressProvider');
  }
  return context;
}
